import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { ChevronDown, Filter } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { getRanking } from 'redux/actions/ranking'
import BaseModal from 'components/CommonComponents/BaseModal'
import ExportToExcelButton from 'components/CommonComponents/ExportToExcelButton'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { isValidNumberString } from 'utils'
import { Button, Capsule, DropdownMenu, IDropdownOption, Loader, SearchInput } from 'components/ui'
import { exportTableToExcel } from './utils'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

// ── Color helpers (mirrored from RankingCard.tsx) ─────────────────────────────

const BASE_COLORS = [
  { r: 255, g: 100, b: 170 },
  { r: 100, g: 140, b: 240 },
  { r: 190, g: 100, b: 210 },
  { r: 255, g: 130, b: 90 },
  { r: 70, g: 210, b: 190 },
  { r: 255, g: 100, b: 130 },
  { r: 150, g: 110, b: 210 },
  { r: 255, g: 150, b: 70 },
  { r: 255, g: 170, b: 100 },
  { r: 120, g: 180, b: 240 },
  { r: 210, g: 120, b: 170 },
  { r: 160, g: 210, b: 120 },
  { r: 255, g: 120, b: 120 },
  { r: 120, g: 160, b: 210 },
  { r: 210, g: 160, b: 120 },
] as const

const adjustSaturation = (
  color: { r: number; g: number; b: number },
  saturationPercent: number,
  alpha: number
): string => {
  const factor = saturationPercent / 100
  const r = Math.round(color.r + (255 - color.r) * (1 - factor))
  const g = Math.round(color.g + (255 - color.g) * (1 - factor))
  const b = Math.round(color.b + (255 - color.b) * (1 - factor))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getBaseColorFromId = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return BASE_COLORS[hash % BASE_COLORS.length]
}

const getAccentColor = (id: string) => adjustSaturation(getBaseColorFromId(id), 70, 1)
const getAccentBg = (id: string) => adjustSaturation(getBaseColorFromId(id), 40, 0.15)

// ── Types ─────────────────────────────────────────────────────────────────────

type SortKey = 'storeName' | 'competitionTitle' | 'rank' | 'value' | 'target' | 'performance'
type SortDir = 'asc' | 'desc'

interface ILeaderboardRow {
  kpiId: string
  competitionTitle: string
  storeId: string
  storeName: string
  altName: string
  rank: any
  value: any
  target: any
  performance: any
}

const TRUNCATE: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
const GRID_TEMPLATE = 'minmax(0,1fr) minmax(0,1fr) 80px 100px 100px 110px'

const parseNum = (v: any): number | null => {
  if (v == null) return null
  const n = parseFloat(String(v))
  return isNaN(n) ? null : n
}

const sortRows = (rows: ILeaderboardRow[], key: SortKey, dir: SortDir): ILeaderboardRow[] => {
  return [...rows].sort((a, b) => {
    const av = a[key]
    const bv = b[key]
    const an = parseNum(av)
    const bn = parseNum(bv)
    if (an != null && bn != null) return dir === 'asc' ? an - bn : bn - an
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    const as = String(av).toLowerCase()
    const bs = String(bv).toLowerCase()
    return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as)
  })
}

// ── Column header ─────────────────────────────────────────────────────────────

interface IColHeaderProps {
  label: string
  sortKey: SortKey
  currentSort: SortKey
  sortDir: SortDir
  onSort: (key: SortKey) => void
  align?: 'flex-start' | 'center'
}

const ColHeader: React.FC<IColHeaderProps> = ({ label, sortKey, currentSort, sortDir, onSort, align = 'center' }) => {
  const isActive = currentSort === sortKey
  const theme = useSelector((state: RootState) => state?.general?.theme)
  const activeColor = theme?.elementsColor ?? colors.blackLight
  return (
    <Flex
      alignItems="center"
      justifyContent={align}
      cursor="pointer"
      onClick={() => onSort(sortKey)}
      // @ts-ignore
      userSelect="none"
      px="10px"
      py="8px"
    >
      <Text
        fontSize="12px"
        fontWeight="500"
        color={isActive ? activeColor : '#94A3B8'}
      >
        {label}
      </Text>
      <Text as="span" fontSize="10px" ml="4px" color={isActive ? activeColor : '#94A3B8'}>
        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </Text>
    </Flex>
  )
}

// ── Cell value with optional units ────────────────────────────────────────────

const CellValue: React.FC<{ value: any }> = ({ value }) => {
  if (value == null) return <Text fontSize="13px" color={colors.greyMain}>—</Text>
  return (
    <Text fontSize="13px" fontWeight="600" color={colors.blackLight}>
      {value}
      {isValidNumberString(String(value)) && (
        <Text as="span" fontSize="11px" fontWeight="400" color={colors.greyDark}>
          {' '}<LocaleText text="general_units" />
        </Text>
      )}
    </Text>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────

const AllStoresModal: React.FC<IProps> = ({ isOpen, onClose }) => {
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [query, setQuery] = useState('')
  const [competitionFilter, setCompetitionFilter] = useState<string | null>(null)
  const [storeFilter, setStoreFilter] = useState<string | null>(null)
  const [altNameFilter, setAltNameFilter] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string | null>(null)

  const dispatch = useDispatch()
  const isFirstRender = useRef(true)

  const { isAllKpisLoading, kpisList, kpisMediaData, rankingReports, rankingFilterList } = useSelector(
    (state: RootState) => state.ranking
  )
  const theme = useSelector((state: RootState) => state?.general?.theme)
  const locale = useSelector((state: RootState) => state.general.locale)
  const translations = useSelector((state: RootState) => state.config.translations)
  const translate = (key: string) => translations[locale]?.[key] ?? key

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    dispatch(getRanking({
      kpis_data: true,
      kpis_list: false,
      force_all_competition_data: true,
      ...(tagFilter ? { rankingTag: tagFilter } : {}),
    }))
  }, [tagFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // ── Build flat rows ──────────────────────────────────────────────────────────

  const allRows = useMemo<ILeaderboardRow[]>(() => {
    const competitions = kpisList.filter((kpi) => kpi.type === 'competition_kpi')
    const rows: ILeaderboardRow[] = []
    competitions.forEach((kpi) => {
      const stores = rankingReports[kpi.id]?.stores ?? []
      const title = kpisMediaData[kpi.id]?.title ?? kpi.id
      stores.forEach((store: any) => {
        rows.push({
          kpiId: kpi.id,
          competitionTitle: title,
          storeId: store.group ?? store.id ?? '',
          storeName: store.name ?? store.group ?? '',
          altName: store.alt_name ?? '',
          rank: store.rank,
          value: store.value,
          target: store.target,
          performance: store.performance,
        })
      })
    })
    return rows
  }, [kpisList, rankingReports, kpisMediaData])

  // ── Dropdown options ─────────────────────────────────────────────────────────

  const competitionOptions = useMemo<IDropdownOption<string | null>[]>(() => {
    const competitions = kpisList.filter((k) => k.type === 'competition_kpi')
    return [
      { id: 'all', label: translate('kpis'), value: null },
      ...competitions.map((k) => ({
        id: k.id,
        label: kpisMediaData[k.id]?.title ?? k.id,
        value: k.id,
      })),
    ]
  }, [kpisList, kpisMediaData])

  const storeOptions = useMemo<IDropdownOption<string | null>[]>(() => {
    const seen = new Map<string, string>()
    allRows.forEach((r) => {
      if (r.storeId && !seen.has(r.storeId)) seen.set(r.storeId, r.storeName)
    })
    const sorted = Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
    return [
      { id: 'all', label: translate('total_contexts'), value: null },
      ...sorted.map(([id, name]) => ({ id, label: name, value: id })),
    ]
  }, [allRows, locale, translations]) // eslint-disable-line react-hooks/exhaustive-deps

  const altNameOptions = useMemo<IDropdownOption<string | null>[]>(() => {
    const seen = new Set<string>()
    allRows.forEach((r) => { if (r.altName) seen.add(r.altName) })
    const sorted = Array.from(seen).sort((a, b) => a.localeCompare(b))
    return [
      { id: 'all', label: translate('users_level_tag'), value: null },
      ...sorted.map((name) => ({ id: name, label: name, value: name })),
    ]
  }, [allRows])

  const tagOptions = useMemo<IDropdownOption<string | null>[]>(() => {
    return rankingFilterList.map((t, i) => {
      const sk = typeof t === 'string' ? t : t.sk
      const label = typeof t === 'string' ? translate(t) : t.name
      return { id: sk, label, value: i === 0 ? null : sk }
    })
  }, [rankingFilterList, locale, translations]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Active filter labels ─────────────────────────────────────────────────────

  const activeCompetitionLabel =
    competitionFilter
      ? (competitionOptions.find((o) => o.value === competitionFilter)?.label as string) ?? translate('kpis')
      : translate('kpis')

  const activeStoreLabel =
    storeFilter
      ? (storeOptions.find((o) => o.value === storeFilter)?.label as string) ?? 'Store'
      : translate('total_contexts')

  const activeAltNameLabel = altNameFilter ?? translate('users_level_tag')

  const defaultTagLabel = tagOptions[0]?.label ?? 'Tag'
  const activeTagLabel =
    tagFilter
      ? (tagOptions.find((o) => o.value === tagFilter)?.label ?? 'Tag')
      : defaultTagLabel

  const hasActiveFilters = !!query || competitionFilter !== null || storeFilter !== null || altNameFilter !== null

  const handleClearAll = () => {
    setQuery('')
    setCompetitionFilter(null)
    setStoreFilter(null)
    setAltNameFilter(null)
  }

  // ── Filter + sort ────────────────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    return allRows.filter((r) => {
      if (competitionFilter && r.kpiId !== competitionFilter) return false
      if (storeFilter && r.storeId !== storeFilter) return false
      if (altNameFilter && r.altName !== altNameFilter) return false
      if (query.trim()) {
        const q = query.trim().toLowerCase()
        if (!r.storeName.toLowerCase().includes(q) && !r.competitionTitle.toLowerCase().includes(q) && !r.altName.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [allRows, competitionFilter, storeFilter, altNameFilter, query])

  const sortedRows = useMemo(() => sortRows(filteredRows, sortKey, sortDir), [filteredRows, sortKey, sortDir])

  const hasData = allRows.length > 0

  const hasAltName = useMemo(() => allRows.some((r) => !!r.altName), [allRows])

  const toNum = (v: any): number | string => {
    const n = parseFloat(String(v ?? ''))
    return isFinite(n) ? n : (v ?? '')
  }

  const handleExport = async () => {
    const headers = [
      translate('store'),
      ...(hasAltName ? [translate('t_name')] : []),
      translate('kpis'),
      translate('rank'),
      translate('ranking_value'),
      translate('ranking_target_title'),
      translate('ranking_perf'),
    ]
    const rows = allRows.map((r) => [
      r.storeName,
      ...(hasAltName ? [r.altName || ''] : []),
      r.competitionTitle,
      r.rank != null ? Number(r.rank) : '',
      toNum(r.value),
      toNum(r.target),
      toNum(r.performance),
    ])
    await exportTableToExcel({ headers, rows, fileName: translate('competitions') })
  }

  // ── Header ───────────────────────────────────────────────────────────────────

  const headerContent = (
    <Text fontWeight="800" fontSize="18px" color={colors.blackLight}>
      <LocaleText text="competitions" />
    </Text>
  )

  // ── Body ─────────────────────────────────────────────────────────────────────

  const bodyContent = (
    <Box px="20px" pb="20px" pt="4px" minH="560px">
      {isAllKpisLoading ? (
        <Loader h="300px" />
      ) : hasData ? (
        <>
          {/* Filter bar */}
          <Flex align="center" wrap="wrap" mb="12px" style={{ gap: '6px' }}>
            <Box flex="1 1 150px" minW="150px" maxW="350px">
              <SearchInput
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                onClear={() => setQuery('')}
                placeholder={translate('ranking_search')}
                variant="filled"
                elementHeight="28px"
              />
            </Box>

            <DropdownMenu
              options={competitionOptions}
              selectedId={competitionFilter ?? 'all'}
              onSelect={(opt) => setCompetitionFilter(opt.value)}
              triggerContent={
                <Flex align="center" style={{ gap: '5px', overflow: 'hidden', minWidth: 0 }}>
                  <Filter size={14} style={{ flexShrink: 0 }} />
                  <Box as="span" style={TRUNCATE}>{activeCompetitionLabel}</Box>
                  <ChevronDown size={12} style={{ flexShrink: 0 }} />
                </Flex>
              }
              triggerVariant="ghost"
              triggerIsActive={competitionFilter !== null}
              triggerActiveColor={theme?.elementsColor}
              menuWidth={240}
              maxListHeight="220px"
              triggerButtonProps={{ h: '28px', px: '8px', fontSize: '12px', fontWeight: '500', flexShrink: 0, maxW: '220px', overflow: 'hidden' }}
            />

            <DropdownMenu
              options={storeOptions}
              selectedId={storeFilter ?? 'all'}
              onSelect={(opt) => setStoreFilter(opt.value)}
              triggerContent={
                <Flex align="center" style={{ gap: '5px', overflow: 'hidden', minWidth: 0 }}>
                  <Filter size={14} style={{ flexShrink: 0 }} />
                  <Box as="span" style={TRUNCATE}>{activeStoreLabel}</Box>
                  <ChevronDown size={12} style={{ flexShrink: 0 }} />
                </Flex>
              }
              triggerVariant="ghost"
              triggerIsActive={storeFilter !== null}
              triggerActiveColor={theme?.elementsColor}
              menuWidth={240}
              maxListHeight="220px"
              triggerButtonProps={{ h: '28px', px: '8px', fontSize: '12px', fontWeight: '500', flexShrink: 0, maxW: '220px', overflow: 'hidden' }}
            />

            {altNameOptions.length > 1 && (
              <DropdownMenu
                options={altNameOptions}
                selectedId={altNameFilter ?? 'all'}
                onSelect={(opt) => setAltNameFilter(opt.value)}
                triggerContent={
                  <Flex align="center" style={{ gap: '5px', overflow: 'hidden', minWidth: 0 }}>
                    <Filter size={14} style={{ flexShrink: 0 }} />
                    <Box as="span" style={TRUNCATE}>{activeAltNameLabel}</Box>
                    <ChevronDown size={12} style={{ flexShrink: 0 }} />
                  </Flex>
                }
                triggerVariant="ghost"
                triggerIsActive={altNameFilter !== null}
                triggerActiveColor={theme?.elementsColor}
                menuWidth={240}
                maxListHeight="220px"
                triggerButtonProps={{ h: '28px', px: '8px', fontSize: '12px', fontWeight: '500', flexShrink: 0, maxW: '220px', overflow: 'hidden' }}
              />
            )}

            {hasActiveFilters && (
              <Button
                variant="ghost"
                h="28px"
                px="4px"
                color="#94A3B8"
                fontSize="11px"
                fontWeight="500"
                flexShrink={0}
                onClick={handleClearAll}
              >
                <LocaleText text="clear_filter" />
              </Button>
            )}

            {/* Spacer to push tag filter to the right */}
            <Box flex="1" />

            {tagOptions.length > 1 && (
              <DropdownMenu
                options={tagOptions}
                selectedId={tagFilter ?? (tagOptions[0]?.id ?? '')}
                onSelect={(opt) => setTagFilter(opt.value)}
                triggerContent={
                  <Flex align="center" style={{ gap: '5px', overflow: 'hidden', minWidth: 0 }}>
                    <Box as="span" style={TRUNCATE}>{activeTagLabel}</Box>
                    <ChevronDown size={12} style={{ flexShrink: 0 }} />
                  </Flex>
                }
                triggerVariant="ghost"
                triggerIsActive={tagFilter !== null}
                triggerActiveColor={theme?.elementsColor}
                menuWidth={240}
                maxListHeight="220px"
                triggerButtonProps={{ h: '28px', px: '8px', fontSize: '12px', fontWeight: '500', flexShrink: 0, maxW: '220px', overflow: 'hidden' }}
              />
            )}

            <ExportToExcelButton actionWithoutArgs={handleExport} />
          </Flex>

          {/* Table */}
          <Box
            bg="white"
            borderRadius="12px"
            boxShadow="0 2px 12px rgba(0,0,0,0.07)"
            overflow="hidden"
          >
            {/* Header row */}
            <Box
              bg="#F7F8FA"
              borderBottom="1px solid #EEF0F2"
              style={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE }}
            >
              <ColHeader label={translate('store')} sortKey="storeName" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="flex-start" />
              <ColHeader label={translate('kpis')} sortKey="competitionTitle" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="flex-start" />
              <ColHeader label={translate('rank')} sortKey="rank" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
              <ColHeader label={translate('ranking_value')} sortKey="value" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
              <ColHeader label={translate('ranking_target_title')} sortKey="target" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
              <ColHeader label={translate('ranking_perf')} sortKey="performance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
            </Box>

            {/* Data rows */}
            {sortedRows.length === 0 ? (
              <Flex justifyContent="center" alignItems="center" h="120px">
                <Text fontSize="14px" color={colors.greyDark}>No results match the selected filters</Text>
              </Flex>
            ) : (
              sortedRows.map((row, i) => {
                const accentColor = getAccentColor(row.kpiId)
                const accentBg = getAccentBg(row.kpiId)
                const isLast = i === sortedRows.length - 1
                return (
                  <Box
                    key={`${row.kpiId}-${row.storeId}`}
                    borderBottom={isLast ? 'none' : '1px solid #EEF0F2'}
                    borderLeft={`4px solid ${accentColor}`}
                    bg="white"
                    // @ts-ignore
                    style={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, alignItems: 'center' }}
                  >
                    <Flex px="10px" py="10px" alignItems="center" style={{ gap: '6px', minWidth: 0 }}>
                      <Text fontSize="13px" fontWeight="600" color={colors.blackLight} isTruncated>
                        {row.storeName || '—'}
                      </Text>
                      {row.altName && (
                        <Capsule label={row.altName} color="#64748B" style={{ flexShrink: 0 }} />
                      )}
                    </Flex>

                    <Box px="10px" py="10px">
                      <Text fontSize="12px" fontWeight="500" color={colors.greyDark} isTruncated>
                        <LocaleText text={row.competitionTitle} />
                      </Text>
                    </Box>

                    <Flex justifyContent="center" px="10px">
                      {row.rank != null ? (
                        <Capsule label={`#${row.rank}`} color={accentColor} size="md" />
                      ) : (
                        <Text fontSize="13px" color={colors.greyMain}>—</Text>
                      )}
                    </Flex>

                    <Flex justifyContent="center" px="10px">
                      <CellValue value={row.value} />
                    </Flex>

                    <Flex justifyContent="center" px="10px">
                      <CellValue value={row.target} />
                    </Flex>

                    <Flex justifyContent="center" px="10px">
                      <CellValue value={row.performance} />
                    </Flex>
                  </Box>
                )
              })
            )}
          </Box>
        </>
      ) : (
        <Flex justifyContent="center" alignItems="center" h="200px" flexDir="column" style={{ rowGap: '8px' }}>
          <Text fontSize="16px" color={colors.greyDark}>
            No leaderboard data available
          </Text>
        </Flex>
      )}
    </Box>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      headerContent={headerContent}
      bodyContent={bodyContent}
      maxWidth="1100px"
      minWidth="600px"
      heightOffset={180}
      closeOnOverlayClick
      closeOnEsc
    />
  )
}

export default AllStoresModal
